// Block registry: single source of truth for every command type.
// Each entry drives the palette, generic Block renderer, and tree mutations.
//
// slot.via:  null  → slot children live at node.children (flat)
//            number → slot children live at node.children[n].children (wrapped)
// field.type: 'text' | 'number' | 'boolean' | 'color' | 'select'
//             | 'var-select' | 'table-var-select' | 'component-select' | 'flag-select'

const COLORS = {
  flow:    '#6ee7b7',
  signal:  '#fda4ba',
  variable:'#c4b5fd',
  flag:    '#fde68a',
  value:   '#93c5fd',
  logical: '#f9a8d4',
  ordinal: '#fca5a5',
  type:    '#fdba74',
  array:   '#fed7aa',
  quant:   '#fcd34d',
  qual:    '#fef08a',
  self:    '#4b5563',
};

const REGISTRY = {

  // ─── Flow ───────────────────────────────────────────────────────────────────

  'conditional/if': {
    label: 'If / Then / Else', shortLabel: 'IF',
    color: COLORS.flow, category: 'Flow', kind: 'statement',
    defaultNode: {
      type: 'conditional/if', comment: -1,
      children: [{ children: [] }, { children: [] }, { children: [] }],
    },
    slots: [
      { label: 'Condition', kind: 'expression', via: 0 },
      { label: 'Then',      kind: 'statement',  via: 1 },
      { label: 'Else',      kind: 'statement',  via: 2 },
    ],
    fields: [],
  },

  'loop/range': {
    label: 'Loop Over Range', shortLabel: 'LOOP',
    color: COLORS.flow, category: 'Flow', kind: 'statement',
    defaultNode: {
      type: 'loop/range', var: '', prefix: '', suffix: '',
      begin: 1, end: 1, comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Body', kind: 'statement', via: 0 }],
    fields: [
      { key: 'var',    type: 'var-select', label: 'Counter Var', inputWidth: '64px' },
      { key: 'begin',  type: 'number',     label: 'From',        inputWidth: '48px' },
      { key: 'end',    type: 'number',     label: 'To',          inputWidth: '48px' },
      { key: 'prefix', type: 'text',       label: 'Prefix',      placeholder: 'prefix', inputWidth: '64px' },
      { key: 'suffix', type: 'text',       label: 'Suffix',      placeholder: 'suffix', inputWidth: '64px' },
    ],
  },

  // ─── Signals ────────────────────────────────────────────────────────────────

  'signal/error': {
    label: 'Throw Error', shortLabel: 'ERROR',
    color: COLORS.signal, category: 'Signals', kind: 'statement',
    defaultNode: { type: 'signal/error', message: 'Error found', comment: -1 },
    slots: [],
    fields: [{ key: 'message', type: 'text', label: 'Message', placeholder: 'Error message' }],
  },

  'signal/warning': {
    label: 'Throw Warning', shortLabel: 'WARNING',
    color: COLORS.signal, category: 'Signals', kind: 'statement',
    defaultNode: { type: 'signal/warning', message: '', comment: -1 },
    slots: [],
    fields: [{ key: 'message', type: 'text', label: 'Message', placeholder: 'Warning message' }],
  },

  'signal/disable': {
    label: 'Disable Component', shortLabel: 'DISABLE',
    color: COLORS.signal, category: 'Signals', kind: 'statement',
    defaultNode: { type: 'signal/disable', comp: '', comment: -1 },
    slots: [],
    fields: [{ key: 'comp', type: 'component-select', label: 'Component' }],
  },

  'signal/disableSelf': {
    label: 'Disable Self', shortLabel: 'DISABLE SELF',
    color: COLORS.signal, category: 'Signals', kind: 'statement',
    defaultNode: { type: 'signal/disableSelf', comment: -1 },
    slots: [],
    fields: [],
  },

  'signal/enable': {
    label: 'Enable Component', shortLabel: 'ENABLE',
    color: COLORS.signal, category: 'Signals', kind: 'statement',
    defaultNode: { type: 'signal/enable', comp: '', comment: -1 },
    slots: [],
    fields: [{ key: 'comp', type: 'component-select', label: 'Component' }],
  },

  'signal/setRecordName': {
    label: 'Set Record Name', shortLabel: 'SET RECORD NAME',
    color: COLORS.signal, category: 'Signals', kind: 'statement',
    defaultNode: {
      type: 'signal/setRecordName', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Name Expression', kind: 'expression', via: 0 }],
    fields: [],
  },

  // ─── Table Signals ──────────────────────────────────────────────────────────

  'signal/table/disable': {
    label: 'Disable Column', shortLabel: 'DISABLE COL',
    color: COLORS.signal, category: 'Table Signals', kind: 'statement',
    defaultNode: { type: 'signal/table/disable', column: 0, comment: -1 },
    slots: [],
    fields: [{ key: 'column', type: 'number', label: 'Column Index' }],
  },

  'signal/table/enable': {
    label: 'Enable Column', shortLabel: 'ENABLE COL',
    color: COLORS.signal, category: 'Table Signals', kind: 'statement',
    defaultNode: { type: 'signal/table/enable', column: 0, comment: -1 },
    slots: [],
    fields: [{ key: 'column', type: 'number', label: 'Column Index' }],
  },

  'signal/table/disableRow': {
    label: 'Disable Row', shortLabel: 'DISABLE ROW',
    color: COLORS.signal, category: 'Table Signals', kind: 'statement',
    defaultNode: { type: 'signal/table/disableRow', comment: -1 },
    slots: [],
    fields: [],
  },

  'signal/table/error': {
    label: 'Throw Table Error', shortLabel: 'TABLE ERROR',
    color: COLORS.signal, category: 'Table Signals', kind: 'statement',
    defaultNode: { type: 'signal/table/error', column: 0, message: '', comment: -1 },
    slots: [],
    fields: [
      { key: 'column',  type: 'number', label: 'Column Index' },
      { key: 'message', type: 'text',   label: 'Message', placeholder: 'Error message' },
    ],
  },

  'signal/table/warning': {
    label: 'Throw Table Warning', shortLabel: 'TABLE WARN',
    color: COLORS.signal, category: 'Table Signals', kind: 'statement',
    defaultNode: { type: 'signal/table/warning', column: 0, message: '', comment: -1 },
    slots: [],
    fields: [
      { key: 'column',  type: 'number', label: 'Column Index' },
      { key: 'message', type: 'text',   label: 'Message', placeholder: 'Warning message' },
    ],
  },

  // ─── Variables ──────────────────────────────────────────────────────────────

  'variable/set': {
    label: 'Set Variable', shortLabel: 'SET VAR',
    color: COLORS.variable, category: 'Variables', kind: 'statement',
    defaultNode: { type: 'variable/set', var: '', children: [], comment: -1 },
    slots: [{ label: 'Value', kind: 'expression', via: null }],
    fields: [{ key: 'var', type: 'var-select', label: 'Variable' }],
  },

  'variable/get': {
    label: 'Get Variable', shortLabel: 'GET VAR',
    color: COLORS.variable, category: 'Variables', kind: 'expression',
    defaultNode: { type: 'variable/get', var: '', comment: -1 },
    slots: [],
    fields: [{ key: 'var', type: 'var-select', label: 'Variable' }],
  },

  'variable/setFrom': {
    label: 'Set Variable From', shortLabel: 'SET FROM',
    color: COLORS.variable, category: 'Variables', kind: 'statement',
    defaultNode: {
      type: 'variable/setFrom', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'Source', kind: 'expression', via: 0 },
      { label: 'Target', kind: 'expression', via: 1 },
    ],
    fields: [],
  },

  'variable/getFrom': {
    label: 'Get Variable From', shortLabel: 'GET FROM',
    color: COLORS.variable, category: 'Variables', kind: 'expression',
    defaultNode: {
      type: 'variable/getFrom', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Source', kind: 'expression', via: 0 }],
    fields: [],
  },

  // ─── Table Variables ─────────────────────────────────────────────────────────

  'variable/table/set': {
    label: 'Set Table Variable', shortLabel: 'SET TABLE VAR',
    color: COLORS.variable, category: 'Table Variables', kind: 'statement',
    defaultNode: { type: 'variable/table/set', var: '', children: [], comment: -1 },
    slots: [{ label: 'Value', kind: 'expression', via: null }],
    fields: [{ key: 'var', type: 'table-var-select', label: 'Variable' }],
  },

  'variable/table/get': {
    label: 'Get Table Variable', shortLabel: 'GET TABLE VAR',
    color: COLORS.variable, category: 'Table Variables', kind: 'expression',
    defaultNode: { type: 'variable/table/get', var: '', comment: -1 },
    slots: [],
    fields: [{ key: 'var', type: 'table-var-select', label: 'Variable' }],
  },

  // ─── Self Values ─────────────────────────────────────────────────────────────

  'self/value': {
    label: "This Field's Value", shortLabel: 'SELF',
    color: COLORS.self, textColor: '#ffffff', category: 'Values', kind: 'expression',
    defaultNode: { type: 'self/value', comment: -1 },
    slots: [],
    fields: [],
  },

  'self/table/value': {
    label: "Column Value", shortLabel: 'COL VALUE',
    color: COLORS.self, textColor: '#ffffff', category: 'Table Variables', kind: 'expression',
    defaultNode: { type: 'self/table/value', column: 0, comment: -1 },
    slots: [],
    fields: [{ key: 'column', type: 'number', label: 'Column Index' }],
  },

  // ─── Flags ───────────────────────────────────────────────────────────────────

  'flags/set': {
    label: 'Set Flag', shortLabel: 'SET FLAG',
    color: COLORS.flag, category: 'Flags', kind: 'statement',
    defaultNode: { type: 'flags/set', flag: '', value: '', comment: -1 },
    slots: [],
    fields: [
      { key: 'flag',  type: 'flag-select',       label: 'Flag' },
      { key: 'value', type: 'flag-value-select', label: 'Value' },
    ],
  },

  'flags/get': {
    label: 'Get Flag', shortLabel: 'GET FLAG',
    color: COLORS.flag, category: 'Flags', kind: 'expression',
    defaultNode: { type: 'flags/get', flag: '', comment: -1 },
    slots: [],
    fields: [{ key: 'flag', type: 'flag-select', label: 'Flag' }],
  },

  'flags/setToChild': {
    label: 'Set Flag to Value', shortLabel: 'FLAG = VALUE',
    color: COLORS.flag, category: 'Flags', kind: 'statement',
    defaultNode: { type: 'flags/setToChild', flag: '', children: [], comment: -1 },
    slots: [{ label: 'Value', kind: 'expression', via: null }],
    fields: [{ key: 'flag', type: 'flag-select', label: 'Flag' }],
  },

  // ─── Expressions ─────────────────────────────────────────────────────────────

  'expression/number': {
    label: 'Number', shortLabel: 'NUMBER',
    color: COLORS.value, category: 'Values', kind: 'expression',
    defaultNode: { type: 'expression/number', value: '0', comment: -1 },
    slots: [],
    fields: [{ key: 'value', type: 'text', label: '', placeholder: '0' }],
  },

  'expression/string': {
    label: 'Text', shortLabel: 'TEXT',
    color: COLORS.value, category: 'Values', kind: 'expression',
    defaultNode: { type: 'expression/string', value: '', comment: -1 },
    slots: [],
    fields: [{ key: 'value', type: 'text', label: '', placeholder: 'text...' }],
  },

  'expression/boolean': {
    label: 'Boolean', shortLabel: 'BOOL',
    color: COLORS.value, category: 'Values', kind: 'expression',
    defaultNode: { type: 'expression/boolean', value: 'true', comment: -1 },
    slots: [],
    fields: [{ key: 'value', type: 'boolean', label: '' }],
  },

  'expression/color': {
    label: 'Color', shortLabel: 'COLOR',
    color: COLORS.value, category: 'Values', kind: 'expression',
    defaultNode: { type: 'expression/color', value: '#ffffff', comment: -1 },
    slots: [],
    fields: [{ key: 'value', type: 'color', label: '' }],
  },

  'expression/currentTime': {
    label: 'Current Time', shortLabel: 'NOW (TIME)',
    color: COLORS.value, category: 'Values', kind: 'expression',
    defaultNode: { type: 'expression/currentTime', comment: -1 },
    slots: [],
    fields: [],
  },

  'expression/currentDate': {
    label: 'Current Date (US)', shortLabel: 'NOW (US)',
    color: COLORS.value, category: 'Values', kind: 'expression',
    defaultNode: { type: 'expression/currentDate', comment: -1 },
    slots: [],
    fields: [],
  },

  'expression/currentUKDate': {
    label: 'Current Date (UK)', shortLabel: 'NOW (UK)',
    color: COLORS.value, category: 'Values', kind: 'expression',
    defaultNode: { type: 'expression/currentUKDate', comment: -1 },
    slots: [],
    fields: [],
  },

  // ─── Logical Operators ───────────────────────────────────────────────────────

  'operator/logical/and': {
    label: 'AND', shortLabel: 'AND',
    color: COLORS.logical, category: 'Logic', kind: 'expression',
    defaultNode: {
      type: 'operator/logical/and', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'A', kind: 'expression', via: 0 },
      { label: 'B', kind: 'expression', via: 1 },
    ],
    fields: [],
  },

  'operator/logical/or': {
    label: 'OR', shortLabel: 'OR',
    color: COLORS.logical, category: 'Logic', kind: 'expression',
    defaultNode: {
      type: 'operator/logical/or', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'A', kind: 'expression', via: 0 },
      { label: 'B', kind: 'expression', via: 1 },
    ],
    fields: [],
  },

  'operator/logical/not': {
    label: 'NOT', shortLabel: 'NOT',
    color: COLORS.logical, category: 'Logic', kind: 'expression',
    defaultNode: {
      type: 'operator/logical/not', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'A', kind: 'expression', via: 0 }],
    fields: [],
  },

  // ─── Ordinal Operators ───────────────────────────────────────────────────────

  'operator/ordinal/eq': {
    label: 'Equal To', shortLabel: '= (EQ)',
    color: COLORS.ordinal, category: 'Compare', kind: 'expression',
    defaultNode: {
      type: 'operator/ordinal/eq', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'A', kind: 'expression', via: 0 },
      { label: 'B', kind: 'expression', via: 1 },
    ],
    fields: [],
  },

  'operator/ordinal/g': {
    label: 'Greater Than', shortLabel: '> (GT)',
    color: COLORS.ordinal, category: 'Compare', kind: 'expression',
    defaultNode: {
      type: 'operator/ordinal/g', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'A', kind: 'expression', via: 0 },
      { label: 'B', kind: 'expression', via: 1 },
    ],
    fields: [],
  },

  'operator/ordinal/geq': {
    label: 'Greater Than or Equal', shortLabel: '>= (GEQ)',
    color: COLORS.ordinal, category: 'Compare', kind: 'expression',
    defaultNode: {
      type: 'operator/ordinal/geq', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'A', kind: 'expression', via: 0 },
      { label: 'B', kind: 'expression', via: 1 },
    ],
    fields: [],
  },

  'operator/ordinal/l': {
    label: 'Less Than', shortLabel: '< (LT)',
    color: COLORS.ordinal, category: 'Compare', kind: 'expression',
    defaultNode: {
      type: 'operator/ordinal/l', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'A', kind: 'expression', via: 0 },
      { label: 'B', kind: 'expression', via: 1 },
    ],
    fields: [],
  },

  'operator/ordinal/leq': {
    label: 'Less Than or Equal', shortLabel: '<= (LEQ)',
    color: COLORS.ordinal, category: 'Compare', kind: 'expression',
    defaultNode: {
      type: 'operator/ordinal/leq', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'A', kind: 'expression', via: 0 },
      { label: 'B', kind: 'expression', via: 1 },
    ],
    fields: [],
  },

  // ─── Type Check Operators ────────────────────────────────────────────────────

  'operator/type/isNum': {
    label: 'Is a Number', shortLabel: 'IS NUM',
    color: COLORS.type, category: 'Type Check', kind: 'expression',
    defaultNode: {
      type: 'operator/type/isNum', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Value', kind: 'expression', via: 0 }],
    fields: [],
  },

  'operator/type/isStr': {
    label: 'Is a String', shortLabel: 'IS STR',
    color: COLORS.type, category: 'Type Check', kind: 'expression',
    defaultNode: {
      type: 'operator/type/isStr', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Value', kind: 'expression', via: 0 }],
    fields: [],
  },

  'operator/type/isEmail': {
    label: 'Is an Email', shortLabel: 'IS EMAIL',
    color: COLORS.type, category: 'Type Check', kind: 'expression',
    defaultNode: {
      type: 'operator/type/isEmail', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Value', kind: 'expression', via: 0 }],
    fields: [],
  },

  'operator/type/isDate': {
    label: 'Is a Date', shortLabel: 'IS DATE',
    color: COLORS.type, category: 'Type Check', kind: 'expression',
    defaultNode: {
      type: 'operator/type/isDate', comment: -1, format: 'us',
      children: [{ children: [] }],
    },
    slots: [{ label: 'Value', kind: 'expression', via: 0 }],
    fields: [
      { key: 'format', type: 'select', label: 'Format',
        options: [{ value: 'us', label: 'US (MM/DD/YYYY)' }, { value: 'uk', label: 'UK (DD/MM/YYYY)' }] },
    ],
  },

  'operator/type/isTime': {
    label: 'Is a Time', shortLabel: 'IS TIME',
    color: COLORS.type, category: 'Type Check', kind: 'expression',
    defaultNode: {
      type: 'operator/type/isTime', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Value', kind: 'expression', via: 0 }],
    fields: [],
  },

  'operator/type/isBlank': {
    label: 'Is Blank', shortLabel: 'IS BLANK',
    color: COLORS.type, category: 'Type Check', kind: 'expression',
    defaultNode: {
      type: 'operator/type/isBlank', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Value', kind: 'expression', via: 0 }],
    fields: [],
  },

  'operator/type/isUndecorated': {
    label: 'Is Undecorated', shortLabel: 'IS UNDECORATED',
    color: COLORS.type, category: 'Type Check', kind: 'expression',
    defaultNode: {
      type: 'operator/type/isUndecorated', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Value', kind: 'expression', via: 0 }],
    fields: [],
  },

  // ─── Array Operators ─────────────────────────────────────────────────────────

  'operator/array/includes': {
    label: 'Includes', shortLabel: 'INCLUDES',
    color: COLORS.array, category: 'Type Check', kind: 'expression',
    defaultNode: {
      type: 'operator/array/includes', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'Array', kind: 'expression', via: 0 },
      { label: 'Item',  kind: 'expression', via: 1 },
    ],
    fields: [],
  },

  'operator/array/isEmpty': {
    label: 'Is Empty', shortLabel: 'IS EMPTY',
    color: COLORS.array, category: 'Type Check', kind: 'expression',
    defaultNode: {
      type: 'operator/array/isEmpty', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Array', kind: 'expression', via: 0 }],
    fields: [],
  },

  // ─── Quantitative Operators ──────────────────────────────────────────────────

  'operator/quant/add': {
    label: 'Add', shortLabel: 'ADD',
    color: COLORS.quant, category: 'Math', kind: 'expression',
    defaultNode: {
      type: 'operator/quant/add', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'A', kind: 'expression', via: 0 },
      { label: 'B', kind: 'expression', via: 1 },
    ],
    fields: [],
  },

  'operator/quant/subtract': {
    label: 'Subtract', shortLabel: 'SUBTRACT',
    color: COLORS.quant, category: 'Math', kind: 'expression',
    defaultNode: {
      type: 'operator/quant/subtract', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'A', kind: 'expression', via: 0 },
      { label: 'B', kind: 'expression', via: 1 },
    ],
    fields: [],
  },

  'operator/quant/multiply': {
    label: 'Multiply', shortLabel: 'MULTIPLY',
    color: COLORS.quant, category: 'Math', kind: 'expression',
    defaultNode: {
      type: 'operator/quant/multiply', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'A', kind: 'expression', via: 0 },
      { label: 'B', kind: 'expression', via: 1 },
    ],
    fields: [],
  },

  'operator/quant/divide': {
    label: 'Divide', shortLabel: 'DIVIDE',
    color: COLORS.quant, category: 'Math', kind: 'expression',
    defaultNode: {
      type: 'operator/quant/divide', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'A', kind: 'expression', via: 0 },
      { label: 'B', kind: 'expression', via: 1 },
    ],
    fields: [],
  },

  'operator/quant/modulus': {
    label: 'Modulus', shortLabel: 'MOD',
    color: COLORS.quant, category: 'Math', kind: 'expression',
    defaultNode: {
      type: 'operator/quant/modulus', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'A', kind: 'expression', via: 0 },
      { label: 'B', kind: 'expression', via: 1 },
    ],
    fields: [],
  },

  'operator/quant/floor': {
    label: 'Round Down', shortLabel: 'FLOOR',
    color: COLORS.quant, category: 'Math', kind: 'expression',
    defaultNode: {
      type: 'operator/quant/floor', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Value', kind: 'expression', via: 0 }],
    fields: [],
  },

  'operator/quant/ceiling': {
    label: 'Round Up', shortLabel: 'CEILING',
    color: COLORS.quant, category: 'Math', kind: 'expression',
    defaultNode: {
      type: 'operator/quant/ceiling', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Value', kind: 'expression', via: 0 }],
    fields: [],
  },

  'operator/quant/round': {
    label: 'Round', shortLabel: 'ROUND',
    color: COLORS.quant, category: 'Math', kind: 'expression',
    defaultNode: {
      type: 'operator/quant/round', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Value', kind: 'expression', via: 0 }],
    fields: [],
  },

  'operator/quant/abs': {
    label: 'Absolute Value', shortLabel: 'ABS',
    color: COLORS.quant, category: 'Math', kind: 'expression',
    defaultNode: {
      type: 'operator/quant/abs', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Value', kind: 'expression', via: 0 }],
    fields: [],
  },

  'operator/quant/max': {
    label: 'Maximum', shortLabel: 'MAX',
    color: COLORS.quant, category: 'Math', kind: 'expression',
    defaultNode: {
      type: 'operator/quant/max', numOfChildren: 2, comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: null, // dynamic via numOfChildren — handled specially in Block
    fields: [],
    variableArity: true,
  },

  'operator/quant/min': {
    label: 'Minimum', shortLabel: 'MIN',
    color: COLORS.quant, category: 'Math', kind: 'expression',
    defaultNode: {
      type: 'operator/quant/min', numOfChildren: 2, comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: null,
    fields: [],
    variableArity: true,
  },

  'operator/quant/timeBetween': {
    label: 'Time Between', shortLabel: 'TIME BETWEEN',
    color: COLORS.quant, category: 'Math', kind: 'expression',
    defaultNode: {
      type: 'operator/quant/timeBetween', returnType: 'Days', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'Start', kind: 'expression', via: 0 },
      { label: 'End',   kind: 'expression', via: 1 },
    ],
    fields: [
      { key: 'returnType', type: 'select', label: 'Unit',
        options: ['Seconds','Minutes','Hours','Days','Weeks','Months','Years']
          .map(v => ({ value: v, label: v })) },
    ],
  },

  // ─── Qualitative Operators ───────────────────────────────────────────────────

  'operator/qual/digits': {
    label: 'Number of Digits', shortLabel: '# DIGITS',
    color: COLORS.qual, category: 'Text', kind: 'expression',
    defaultNode: {
      type: 'operator/qual/digits', before: -1, after: -1, comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Value', kind: 'expression', via: 0 }],
    fields: [],
  },

  'operator/qual/words': {
    label: 'Number of Words', shortLabel: '# WORDS',
    color: COLORS.qual, category: 'Text', kind: 'expression',
    defaultNode: {
      type: 'operator/qual/words', before: -1, after: -1, comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Value', kind: 'expression', via: 0 }],
    fields: [],
  },

  'operator/qual/charLength': {
    label: 'Character Length', shortLabel: 'CHAR LEN',
    color: COLORS.qual, category: 'Text', kind: 'expression',
    defaultNode: {
      type: 'operator/qual/charLength', whitespace: 'count', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Value', kind: 'expression', via: 0 }],
    fields: [
      { key: 'whitespace', type: 'select', label: 'Whitespace',
        options: [{ value: 'count', label: 'Count' }, { value: 'nocount', label: 'Don\'t Count' }] },
    ],
  },

  'operator/qual/concat': {
    label: 'Concatenate', shortLabel: 'CONCAT',
    color: COLORS.qual, category: 'Text', kind: 'expression',
    defaultNode: {
      type: 'operator/qual/concat', comment: -1,
      children: [{ children: [] }, { children: [] }],
    },
    slots: [
      { label: 'A', kind: 'expression', via: 0 },
      { label: 'B', kind: 'expression', via: 1 },
    ],
    fields: [],
  },

  'operator/qual/day': {
    label: 'Day of Date', shortLabel: 'DAY',
    color: COLORS.qual, category: 'Date / Time', kind: 'expression',
    defaultNode: {
      type: 'operator/qual/day', before: -1, after: -1, format: 'us', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Date', kind: 'expression', via: 0 }],
    fields: [
      { key: 'format', type: 'select', label: 'Format',
        options: [{ value: 'us', label: 'US' }, { value: 'uk', label: 'UK' }] },
    ],
  },

  'operator/qual/month': {
    label: 'Month of Date', shortLabel: 'MONTH',
    color: COLORS.qual, category: 'Date / Time', kind: 'expression',
    defaultNode: {
      type: 'operator/qual/month', before: -1, after: -1, format: 'us', comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Date', kind: 'expression', via: 0 }],
    fields: [
      { key: 'format', type: 'select', label: 'Format',
        options: [{ value: 'us', label: 'US' }, { value: 'uk', label: 'UK' }] },
    ],
  },

  'operator/qual/year': {
    label: 'Year of Date', shortLabel: 'YEAR',
    color: COLORS.qual, category: 'Date / Time', kind: 'expression',
    defaultNode: {
      type: 'operator/qual/year', before: -1, after: -1, comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Date', kind: 'expression', via: 0 }],
    fields: [],
  },

  'operator/qual/hour': {
    label: 'Hour of Time', shortLabel: 'HOUR',
    color: COLORS.qual, category: 'Date / Time', kind: 'expression',
    defaultNode: {
      type: 'operator/qual/hour', before: -1, after: -1, comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Time', kind: 'expression', via: 0 }],
    fields: [],
  },

  'operator/qual/minute': {
    label: 'Minute of Time', shortLabel: 'MINUTE',
    color: COLORS.qual, category: 'Date / Time', kind: 'expression',
    defaultNode: {
      type: 'operator/qual/minute', before: -1, after: -1, comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Time', kind: 'expression', via: 0 }],
    fields: [],
  },

  'operator/qual/second': {
    label: 'Second of Time', shortLabel: 'SECOND',
    color: COLORS.qual, category: 'Date / Time', kind: 'expression',
    defaultNode: {
      type: 'operator/qual/second', before: -1, after: -1, comment: -1,
      children: [{ children: [] }],
    },
    slots: [{ label: 'Time', kind: 'expression', via: 0 }],
    fields: [],
  },
};

export default REGISTRY;

// Icons for each palette category — used in both the palette sidebar and block headers.
export const CATEGORY_ICONS = {
  'Flow':            '◇',
  'Signals':         '◎',
  'Table Signals':   '⊞',
  'Variables':       '≔',
  'Table Variables': '⊡',
  'Flags':           '⚑',
  'Values':          '◆',
  'Logic':           '∧',
  'Compare':         '≈',
  'Type Check':      '⊘',
  'Math':            '∑',
  'Text':            '¶',
  'Date / Time':     '⊙',
};

// Palette categories: ordered list of { label, types[] }
export const PALETTE_CATEGORIES = [
  {
    label: 'Flow',
    types: ['conditional/if', 'loop/range'],
  },
  {
    label: 'Signals',
    types: [
      'signal/error', 'signal/warning',
      'signal/disable', 'signal/enable', 'signal/disableSelf',
      'signal/setRecordName',
    ],
  },
  {
    label: 'Table Signals',
    types: [
      'signal/table/error', 'signal/table/warning',
      'signal/table/disable', 'signal/table/enable', 'signal/table/disableRow',
    ],
  },
  {
    label: 'Variables',
    types: ['variable/set', 'variable/get', 'variable/setFrom', 'variable/getFrom', 'self/value'],
  },
  {
    label: 'Table Variables',
    types: ['variable/table/set', 'variable/table/get', 'self/table/value'],
  },
  {
    label: 'Flags',
    types: ['flags/set', 'flags/get', 'flags/setToChild'],
  },
  {
    label: 'Values',
    types: [
      'expression/number', 'expression/string', 'expression/boolean', 'expression/color',
      'expression/currentDate', 'expression/currentUKDate', 'expression/currentTime',
    ],
  },
  {
    label: 'Logic',
    types: ['operator/logical/and', 'operator/logical/or', 'operator/logical/not'],
  },
  {
    label: 'Compare',
    types: [
      'operator/ordinal/eq', 'operator/ordinal/g', 'operator/ordinal/geq',
      'operator/ordinal/l', 'operator/ordinal/leq',
    ],
  },
  {
    label: 'Type Check',
    types: [
      'operator/type/isNum', 'operator/type/isStr', 'operator/type/isEmail',
      'operator/type/isDate', 'operator/type/isTime', 'operator/type/isBlank',
      'operator/type/isUndecorated', 'operator/array/includes', 'operator/array/isEmpty',
    ],
  },
  {
    label: 'Math',
    types: [
      'operator/quant/add', 'operator/quant/subtract', 'operator/quant/multiply',
      'operator/quant/divide', 'operator/quant/modulus',
      'operator/quant/floor', 'operator/quant/ceiling', 'operator/quant/round', 'operator/quant/abs',
      'operator/quant/max', 'operator/quant/min', 'operator/quant/timeBetween',
    ],
  },
  {
    label: 'Text',
    types: ['operator/qual/concat', 'operator/qual/charLength', 'operator/qual/digits', 'operator/qual/words'],
  },
  {
    label: 'Date / Time',
    types: [
      'operator/qual/day', 'operator/qual/month', 'operator/qual/year',
      'operator/qual/hour', 'operator/qual/minute', 'operator/qual/second',
    ],
  },
];
